import { useSyncExternalStore } from "react";

// #632 — the entitlement override, extracted from entitlementFlip.ts into a
// module with NO app imports.
//
// ⚠️ THE ZERO-IMPORT RULE IS THE WHOLE POINT. useAuth.tsx now folds this
// override into the `isPro` it publishes, so useAuth has to be able to import
// it. It cannot import entitlementFlip.ts, because:
//
//   useAuth.tsx → entitlementFlip.ts → useMessageLimit.ts → useAuth.tsx
//                                      ^ clearPersistedLimitFlag
//
// #632 predicted this would be clean ("entitlementFlip.ts imports supabase and
// proSetupFlow, not useAuth") — it also imports clearPersistedLimitFlag from
// useMessageLimit, whose line 3 is `import { useAuth }`. The cycle is real.
// A circular import through the auth provider is exactly the module-init
// hazard that produces "X is not defined" at load, so it is broken here
// rather than tolerated.
//
// Keep this file importing nothing but React. entitlementFlip.ts owns the
// supabase/auth wiring and imports THIS; never the other way around.

let entitlementOverride: boolean | null = null;
let entitlementOverrideUserId: string | null = null;
const listeners = new Set<() => void>();

const notify = () => {
  listeners.forEach((listener) => listener());
};

export const subscribeEntitlementOverride = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getEntitlementOverrideSnapshot = (): boolean | null => entitlementOverride;

/** The user who earned the override, for the sign-out / other-user reset. */
export const getEntitlementOverrideUserId = (): string | null => entitlementOverrideUserId;

/** Records a confirmed free→pro flip for `userId`. */
export const setEntitlementOverride = (userId: string): void => {
  entitlementOverride = true;
  entitlementOverrideUserId = userId;
  notify();
};

export const resetEntitlementOverride = (): void => {
  if (entitlementOverride === null && entitlementOverrideUserId === null) return;
  entitlementOverride = null;
  entitlementOverrideUserId = null;
  notify();
};

/** React hook: true once a free→pro flip has been detected this session. */
export const useEntitlementOverride = (): boolean | null =>
  useSyncExternalStore(subscribeEntitlementOverride, getEntitlementOverrideSnapshot, () => null);
